import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import ability from 'config/ability';
import { CORPORATE_CLIENT_COUNTRIES } from 'constants/corporateClients';
import { AMPLITUDE_EVENT_TYPES } from 'constants/amplitude';
import {
  useEditBillingInformation,
  useGetBillingInformation,
} from 'services/reactQueryHooks/corporations';
import useAmplitude from 'utils/hooks/useAmplitude';
import { isOnOrAfterWalletGoLive } from 'utils/walletUtil';
import { isCompanyDetailsMissing, toBillingInformation } from './helper';

const useCompanyDetailsModal = () => {
  const { sendAmplitudeEvent } = useAmplitude();
  const isMobile = useSelector(state => state.settings?.isMobileView);
  const {
    adminRole,
    currentCorporation,
    restrictedCorporateClientProspect,
    emailNotVerified,
  } = useSelector(state => state.user || {});

  // From the wallet go-live, a UK org without an Allstar credit account gives its own address and
  // tax number (Allstar used to collect them). Staff are never asked on a customer's behalf.
  const isEligible =
    !adminRole &&
    !restrictedCorporateClientProspect &&
    !emailNotVerified &&
    currentCorporation?.country === CORPORATE_CLIENT_COUNTRIES.GB &&
    !currentCorporation?.creditAccountStatus &&
    isOnOrAfterWalletGoLive() &&
    ability.can('edit', 'Billing');

  const { data: billingInformation } = useGetBillingInformation({ enabled: isEligible });
  const {
    mutate: editBillingInformation,
    isLoading: isEditBillingInformationLoading,
  } = useEditBillingInformation();

  const isVisible = isEligible && isCompanyDetailsMissing(billingInformation);

  useEffect(() => {
    isVisible && sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.companyDetailsModalViewed);
  }, [isVisible, sendAmplitudeEvent]);

  const handleSubmit = useCallback(
    ({ formData }) => {
      editBillingInformation(toBillingInformation(billingInformation, formData));
    },
    [billingInformation, editBillingInformation],
  );

  return {
    isVisible,
    isMobile,
    isLoading: isEditBillingInformationLoading,
    countryCode: currentCorporation?.country,
    onSubmit: handleSubmit,
  };
};

export default useCompanyDetailsModal;
